/**
 * Trade Pack Type Definitions
 *
 * Trade packs are tier-agnostic plug-ins that define how a trade-specific
 * tenant operates: which service categories exist, what intake / scope /
 * completion fields are captured, what compliance flows are required, what
 * customer-facing report templates are issued, what terminology to use, and
 * what dashboard widgets / routes are surfaced.
 *
 * Every pack must work cleanly on BOTH tiers (Lite shell AND Pro shells).
 *
 * USAGE:
 * - Import types: import { TradePack } from '@/types/trade-pack.types'
 * - Access via hook: const { pack } = useTradePack()
 *
 * FIRESTORE COLLECTION: trade_packs/{packId}
 *
 * SECURITY: trade_packs is GLOBAL (not tenant-scoped). Reads are
 * authenticated; writes are admin-only (firestore.rules). Pack docs are
 * keyed by pack ID, NOT by a tenantId field, so the tenantIdReadUnion
 * pattern doesn't apply.
 *
 * @see docs/TRADE_PACKS.md - Pack schema + pack-engine invariants
 * @see CLAUDE.md - Architecture Standards (tier/pack model)
 */

import type { FieldDef } from '@/data/service-configs';

/**
 * A service category exposed by the pack. Replaces the hardcoded
 * DealCategory enum at runtime — pack categories appear as new string values
 * alongside the legacy DealCategory enum members.
 *
 * The legacy `presiteDefault` ('always' | 'never' | 'conditional') is omitted
 * here because pre-site flow is Pro-tier-only and pack-agnostic; tenants on
 * Lite skip pre-site entirely. If a future pack needs custom pre-site
 * behaviour, add it as an optional field rather than baking it in.
 */
export interface ServiceCategoryDef {
  /** Stable category key. Pack categories use kebab-case (e.g. 'switchboard-upgrade');
   *  legacy DealCategory values use SCREAMING_SNAKE strings (e.g. 'NBN'). */
  key: string;

  /** Display label (e.g. 'Switchboard Upgrade'). */
  label: string;

  /** Lucide icon name (e.g. 'Zap', 'Globe'). */
  icon: string;

  /** Tailwind color class fragment (e.g. 'amber', 'blue'). */
  color: string;

  /** Fields captured when the deal/job is first created. */
  intakeFieldDefs: FieldDef[];

  /** Fields captured during a pre-site site survey (Pro tier only). */
  scopeFieldDefs: FieldDef[];

  /** Fields captured at job completion. */
  completionFieldDefs: FieldDef[];

  /** Optional reference to a ComplianceFlow attached to this category. */
  complianceFlowKey?: string;
}

/**
 * A service-request type the pack offers when logging a customer-initiated
 * change ("NBN Upgrade", "Test & Tag Booking", "Gate Not Closing").
 *
 * These replace the hardcoded `ServiceRequestType` union at runtime, the same
 * way `ServiceCategoryDef` replaces `DealCategory`. Stored request types stay
 * plain strings on the record, so a request logged under one pack still reads
 * back after the tenant's pack changes — the label lookup falls back rather
 * than rendering blank.
 *
 * `key` must be stable: it is written onto every service_requests doc and is
 * what stage helpers like `usesNBNPipeline()` match on.
 */
export interface ServiceRequestTypeDef {
  /** Stable key persisted on the record (e.g. 'nbn_upgrade', 'test_and_tag'). */
  key: string;

  /** Button label (e.g. 'NBN Upgrade'). */
  label: string;

  /** One-line explanation shown under the label in the intake modal. */
  description: string;
}

/**
 * Evidence types a compliance step can require.
 */
export type ComplianceEvidenceType =
  | 'photo'         // Camera/file upload
  | 'serial'        // Serial number / barcode capture
  | 'cert-number'   // Free-text certificate / reference number
  | 'pass-fail'     // Boolean pass/fail toggle
  | 'reading'       // Numeric measurement (e.g. RCD leakage in mA)
  | 'signature';    // Captured signature (electrician, customer, etc.)

/**
 * A single step within a compliance flow. Composes into a ComplianceFlow.
 *
 * Multiple steps make up regulator-accepted artifacts like the NSW
 * Certificate of Electrical Safety (6+ steps).
 */
export interface ComplianceStep {
  /** Stable step key, unique within the flow (e.g. 'rcd-test'). */
  key: string;

  /** Display label (e.g. 'RCD Test'). */
  label: string;

  /** What the tech captures to satisfy this step. */
  evidenceType: ComplianceEvidenceType;

  /** If true, blocks job completion until satisfied. */
  required: boolean;

  /** Extra captured data for this step, keyed by field name. */
  metadata?: Record<string, FieldDef>;

  /** Conditionally skip this step based on an earlier step's answer. */
  conditionalOn?: { stepKey: string; value: unknown };
}

/**
 * Regulator metadata for compliance flows that produce regulated artifacts.
 *
 * Presence of this field on a flow marks the flow as "regulated" — pack
 * versioning auto-upgrades on regulator-required changes (Phase 4.5).
 */
export interface RegulatorMetadata {
  /** Jurisdiction code (e.g. 'NSW', 'VIC'). */
  jurisdiction: string;

  /** Standard reference (e.g. 'AS/NZS 3000:2018'). */
  standard: string;

  /** How the artifact reaches the regulator. */
  submissionMethod?: 'self-lodge' | 'portal-submit' | 'paper';
}

/**
 * A composable compliance flow attached to one or more service categories.
 *
 * NSW Certificate of Electrical Safety = one ComplianceFlow with 6+ steps
 * outputting a regulator-accepted PDF via the Native Proposals template
 * engine.
 */
export interface ComplianceFlow {
  /** Stable flow key (e.g. 'nsw-coes'). */
  key: string;

  /** Display label (e.g. 'NSW Certificate of Electrical Safety'). */
  label: string;

  /** Service category keys this flow attaches to. */
  appliesTo: string[];

  /** If true, blocks job completion until all required steps are satisfied. */
  required: boolean;

  /** Ordered list of steps composing the flow. */
  steps: ComplianceStep[];

  /** Native Proposals report template key. The flow's output artifact. */
  outputTemplate?: string;

  /** Regulator metadata for regulated industries. Presence marks the flow as
   *  regulated for pack-versioning purposes. */
  regulatorMetadata?: RegulatorMetadata;
}

/**
 * Reference to a Native Proposals report template shipped by the pack.
 *
 * The full template body lives in the Native Proposals template engine
 * (tenant-scoped); the pack ships defaults that tenants can clone-customize
 * via the `source: 'pack' | 'tenant'` discriminator (Phase 4 work).
 */
export interface ReportTemplate {
  /** Stable template key. */
  key: string;

  /** Display name shown to tenant admins. */
  name: string;

  /** When this template fires automatically (or 'manual' for on-demand). */
  triggerOn?: 'job.completed' | 'fault.resolved' | 'compliance.flow.completed' | 'manual';

  /** Native Proposals template document ID. Resolved at runtime. */
  templateId?: string;
}

/**
 * Reference to a dashboard widget the pack wants surfaced.
 *
 * The widget implementations themselves live in the codebase — the pack only
 * references them by key. Widget keys not registered in the codebase are
 * silently ignored.
 */
export interface DashboardWidgetRef {
  /** Widget implementation key (e.g. 'compliance-tests-due', 'mrr-summary'). */
  key: string;

  /** Tier compatibility — some widgets only make sense on one tier. */
  appliesTo?: ('lite' | 'pro')[];

  /** Optional widget-specific config passed at render time. */
  config?: Record<string, unknown>;
}

/**
 * Tier-prefix-agnostic route configuration.
 *
 * Path segments are relative — the shell decides the prefix:
 *   Pro:  /home/{routeKey}
 *   Lite: /app/{routeKey}
 *
 * @see docs/TRADE_PACKS.md - Pack-route convention
 */
export interface TradePackRoutes {
  /** Pack-specific routes to expose (e.g. ['compliance-schedule']). */
  enabled: string[];

  /** Base routes the pack hides from nav (e.g. ['services'] for non-telco packs). */
  hidden: string[];
}

/**
 * Complete trade pack configuration.
 *
 * @example
 * // Firestore document structure
 * trade_packs/electrician {
 *   id: 'electrician',
 *   name: 'Electrician',
 *   appliesTo: ['lite', 'pro'],
 *   serviceCategories: [...],
 *   complianceFlows: [{ key: 'nsw-coes', ... }],
 *   ...
 * }
 */
export interface TradePack {
  /** Unique pack identifier (Firestore document ID). */
  id: string;

  /** Display name (e.g. 'Electrician'). */
  name: string;

  /** Which tiers this pack supports. Most packs are ['lite', 'pro']. */
  appliesTo: ('lite' | 'pro')[];

  /** Service categories the pack exposes. Replaces DealCategory enum at runtime. */
  serviceCategories: ServiceCategoryDef[];

  /** Compliance flows the pack defines. Attached to categories via complianceFlowKey. */
  complianceFlows: ComplianceFlow[];

  /**
   * Request types offered when logging a customer-initiated service request.
   *
   * Optional for back-compat: a pack that omits this falls back to the static
   * telco list in `service-request.types.ts`, which is what every pack did
   * before this field existed.
   */
  serviceRequestTypes?: ServiceRequestTypeDef[];

  /** String overrides — `{ tech: 'Sparky', fault: 'Callback' }`. */
  terminology: Record<string, string>;

  /** Tier-prefix-agnostic route config. */
  routes: TradePackRoutes;

  /** Customer-facing report templates the pack ships. */
  reportTemplates: ReportTemplate[];

  /** Dashboard widget references — tier-aware. */
  dashboardWidgets: DashboardWidgetRef[];

  /** Trade-specific pricing seeds (default labour rates, common item SKUs, etc.). */
  pricingHints?: Record<string, unknown>;

  /** Pack version. Increment for any data change. */
  version?: number;

  /** Change discriminator drives auto-upgrade behaviour (Phase 4.5).
   *  - 'regulator-required': auto-upgrades on regulated packs
   *  - 'cosmetic': opt-in per tenant
   *  - 'breaking': requires explicit migration */
  versionChangeType?: 'regulator-required' | 'cosmetic' | 'breaking';

  /** ISO timestamp of last update. */
  updatedAt?: string;
}

/**
 * Default pack ID used as runtime fallback when tenant.tradePackId is missing.
 * Re-bundles today's hardcoded SERVICE_CONFIGS.
 */
export const DEFAULT_TRADE_PACK_ID = 'telco-installer';
